<template>
  <div data-qa="inapp-test-send-dialog">
    <el-dialog
      width="600px"
      :visible="true"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :modal="false"
      :before-close="close"
    >
      <template #title>
        <div class="el-dialog__title" data-qa="inapp-test-send-dialog-title">
          {{ $t('CampaignForm_Preview_Send_Inapp_Title') }}
        </div>
      </template>
      <div
        v-if="profileLabel"
        class="profile-description"
        data-qa="inapp-test-send-dialog-profile-description"
      >
        {{
          $t('CampaignForm_Preview_Send_Description', { profile: profileLabel })
        }}
      </div>
      <div
        v-else
        class="profile-description profile-description--muted"
        data-qa="inapp-test-send-dialog-profile-description-empty"
      >
        {{ $t('CampaignForm_Preview_Send_Description_NoProfile') }}
      </div>
      <label class="inline">
        {{ $t('CampaignForm_Preview_Send_Inapp_To') }}
      </label>
      <form-item class="inline" prop="recipients">
        <el-select
          v-model="recipients"
          :multiple-limit="recipientsLimit"
          :placeholder="
            $t('CampaignForm_Preview_Send_Inapp_Recipient_Placeholder')
          "
          multiple
          filterable
          allow-create
          default-first-option
          data-qa="inapp-test-send-dialog-recipients"
        >
          <el-option
            v-for="item in savedOptions"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
        <div
          :class="['recipients-hint', { 'is-at-limit': isAtRecipientsLimit }]"
          data-qa="inapp-test-send-dialog-recipients-hint"
        >
          <i
            v-if="isAtRecipientsLimit"
            class="el-icon-warning-outline recipients-hint-icon"
          />
          {{ recipientsHint }}
        </div>
      </form-item>
      <div v-loading="loading" data-qa="inapp-test-send-dialog-body">
        <div
          v-if="results.length"
          class="results-ct"
          data-qa="inapp-test-send-dialog-results"
        >
          <div
            v-for="recipient in results"
            :key="recipient.identifier"
            class="result-row"
            data-qa="inapp-test-send-dialog-result-row"
          >
            <div
              class="result-identifier"
              data-qa="inapp-test-send-dialog-result-identifier"
            >
              <span
                v-if="identifierTypeLabel(recipient)"
                class="result-identifier-type"
                data-qa="inapp-test-send-dialog-result-identifier-type"
              >
                {{ identifierTypeLabel(recipient) }}:
              </span>
              <span
                class="result-identifier-value"
                data-qa="inapp-test-send-dialog-result-identifier-value"
              >
                {{ recipient.identifier }}
              </span>
            </div>
            <div
              v-if="recipient.error"
              class="result-error"
              data-qa="inapp-test-send-dialog-result-error"
            >
              {{ recipient.error_message }}
            </div>
            <div
              v-else-if="recipient.deduplicated"
              class="result-deduplicated"
              data-qa="inapp-test-send-dialog-result-deduplicated"
            >
              {{ $t('CampaignForm_Preview_Send_Inapp_AlreadySent') }}
            </div>
            <div
              v-else
              class="result-success"
              data-qa="inapp-test-send-dialog-result-success"
            >
              <span
                class="result-status"
                data-qa="inapp-test-send-dialog-result-status"
              >
                {{ $t('CampaignForm_Preview_Send_Inapp_Sent') }}
              </span>
              <ul
                v-if="recipientTargets(recipient).length"
                class="result-targets"
                data-qa="inapp-test-send-dialog-result-targets"
              >
                <li
                  v-for="target in recipientTargets(recipient)"
                  :key="`${target.application_id}-${target.platform}`"
                  class="result-target-row"
                  :data-qa="
                    `inapp-test-send-dialog-result-target-${target.application_id}-${target.platform}`
                  "
                >
                  {{ targetLabel(target) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="send-button">
        <el-button
          :disabled="disableInput"
          icon="el-icon-s-promotion"
          type="primary"
          data-qa="inapp-test-send-dialog-send-button"
          @click="sendPreview"
        >
          {{ $t('CampaignForm_Preview_Send_Inapp_Send_Button') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FormItem from '@/common/components/form/FormItem.vue'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import { Message } from 'element-ui'
import { getProfileLabel } from '@/modules/campaign-manage/helpers/ProfileLabelHelper'

const RECIPIENTS_LIMIT = 10

export default {
  name: 'InappTestSendDialog',
  components: { FormItem },
  props: {
    dialogVisible: {
      type: Boolean,
      required: true
    },
    /**
     * Bundled preview metadata (campaign, message_type, variant_key, workflow_action,
     * language, profile_id, customAttributes) — same shape PushTestSendDialog accepts —
     * which the dashboard hands to the unified `campaign-test-sends` endpoint so the
     * backend's CampaignPreviewBuilder can rehydrate the in-app context.
     */
    preview: {
      type: Object,
      required: true
    },
    /**
     * The HTML body the dashboard already rendered for the in-app preview iframe.
     * Required for non-native in-app types; sent verbatim to the backend, persisted,
     * and served back to the SDK via /push/api/campaignPreview when the recipient's
     * device polls the in-app queue. The backend does not re-render or personalise it.
     */
    html: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * The native in-app payload string (raw text, form encoding, or JSON-encoded
     * key/value pairs depending on the campaign config) that the SDK consumes
     * in-process. Required when {@see this.inappNative} is true; ignored otherwise.
     */
    inappData: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * Selects which payload shape is sent. When true the dialog dispatches a native
     * in-app envelope (`inapp_native = 1`) and persists `inappData`; when false the
     * HTML pipeline is used. Mirrors the runtime behaviour of
     * MessagePayloadGenerator::getInappMessage().
     */
    inappNative: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      recipients: [],
      savedOptions: [],
      results: [],
      loading: false
    }
  },
  computed: {
    hasPayload() {
      return this.inappNative ? !!this.inappData : !!this.html
    },
    disableInput() {
      return this.loading || !this.recipients.length || !this.hasPayload
    },
    recipientsLimit() {
      return RECIPIENTS_LIMIT
    },
    isAtRecipientsLimit() {
      return this.recipients.length >= RECIPIENTS_LIMIT
    },
    recipientsHint() {
      // El-select's `multiple-limit` silently ignores selections past the cap, which
      // looks like a bug to the operator. Surface a count-of-cap line under the field
      // so they can see the limit before they hit it, and a stronger warning once they do.
      const count = this.recipients.length
      const limit = RECIPIENTS_LIMIT
      if (this.isAtRecipientsLimit) {
        return this.$t('CampaignForm_Preview_Send_Recipients_LimitReached', {
          limit
        })
      }
      return this.$t('CampaignForm_Preview_Send_Recipients_Count', {
        count,
        limit
      })
    },
    profileLabel() {
      return getProfileLabel(this.preview && this.preview.selected_profile)
    }
  },
  watch: {
    recipients(value) {
      this.savedOptions = [...new Set([...(value || []), ...this.savedOptions])]
    }
  },
  methods: {
    close() {
      this.$emit('update:dialogVisible', false)
    },
    recipientTargets(recipient) {
      // The backend now returns a per-(app, platform) `targets` list; older payloads
      // only carried separate `application_ids` and `platform_types` arrays. Fall
      // back to a synthesised list so a stale browser tab against a freshly-deployed
      // backend (or vice versa) keeps rendering something useful.
      if (Array.isArray(recipient.targets) && recipient.targets.length) {
        return recipient.targets
      }
      const apps = recipient.application_ids || []
      const platforms = recipient.platform_types || []
      const fallback = []
      apps.forEach(applicationId => {
        platforms.forEach(platform => {
          fallback.push({
            application_id: applicationId,
            application_name: null,
            platform
          })
        })
      })
      return fallback
    },
    targetLabel(target) {
      // Prefer the human-readable app name; fall back to the numeric id so the operator
      // still has a hook into the project's Apps screen when the project is mid-rename.
      const appLabel =
        target.application_name || `App #${target.application_id}`
      return `${appLabel} (${this.formatPlatform(target.platform)})`
    },
    formatPlatform(platform) {
      // Backend stores platform types as `ios`/`android` to match the SDK contract;
      // the dashboard surfaces them as proper-cased product names. Falls back to
      // the raw value so an unexpected platform isn't swallowed silently.
      switch (platform) {
        case 'ios':
          return 'iOS'
        case 'android':
          return 'Android'
        default:
          return platform
      }
    },
    identifierTypeLabel(recipient) {
      // Label echoes which Profile field the operator's input matched so they can
      // confirm at a glance which axis of the profile they actually targeted —
      // useful when one profile is reachable via several identifiers.
      switch (recipient && recipient.identifier_type) {
        case 'profile_id':
          return this.$t('CampaignForm_Preview_Send_IdentifierType_ProfileId')
        case 'email':
          return this.$t('CampaignForm_Preview_Send_IdentifierType_Email')
        case 'user_id':
          return this.$t('CampaignForm_Preview_Send_IdentifierType_UserId')
        default:
          return null
      }
    },
    sendPreview() {
      this.loading = true
      this.results = []
      const data = {
        campaign: this.preview.campaign,
        message_type: this.preview.message_type,
        variant_key: this.preview.variant_key,
        workflow_action: this.preview.workflow_action,
        language: this.preview.language,
        profile_id: this.preview.profile_id,
        customAttributes: this.preview.customAttributes,
        recipients: this.recipients,
        inapp_native: this.inappNative
      }
      if (this.inappNative) {
        data.inapp_data = this.inappData
      } else {
        data.html = this.html
      }
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaign-test-sends`,
          data
        })
      )
        .then(({ data }) => {
          this.results = data.recipients || []
          if (data.error) {
            this.$message({
              message:
                data.error_message ||
                this.$t('CampaignForm_Preview_Send_Inapp_PartialFailure'),
              type: 'error',
              duration: 5000
            })
          }
        })
        .catch(error => {
          Message.closeAll()
          this.$message({
            message: error.message,
            type: 'error',
            duration: 5000
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.profile-description {
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}
.profile-description--muted {
  color: #909399;
  font-style: italic;
}

.inline {
  display: inline-block;
}
.el-form-item {
  margin-right: 5px;
  width: 90%;
}

.recipients-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

.recipients-hint.is-at-limit {
  color: #e6a23c;
}

.recipients-hint-icon {
  margin-right: 4px;
}

.results-ct {
  margin-top: 16px;
  border-top: 1px solid #e4e7ed;
  padding-top: 12px;
}

.result-row {
  margin-bottom: 12px;
}

.result-row + .result-row {
  border-top: 1px solid #e4e7ed;
  padding-top: 12px;
}

.result-identifier {
  font-weight: 600;
  margin-bottom: 4px;
}

.result-identifier-type {
  color: #909399;
  font-weight: 500;
  margin-right: 4px;
}

.result-success {
  color: #67c23a;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.result-targets {
  color: #606266;
  font-size: 12px;
  list-style: none;
  margin: 4px 0 0;
  padding-left: 12px;
}

.result-target-row {
  padding: 1px 0;
}

.result-error {
  color: #f56c6c;
  margin-top: 4px;
}

.result-deduplicated {
  color: #909399;
  margin-top: 4px;
  font-style: italic;
}

.send-button {
  display: flex;
  justify-content: right;
  margin-top: 12px;
}

/deep/ .el-dialog__header {
  background-color: #1d87c8;
}

/deep/ .el-dialog__headerbtn {
  border-color: #ffffff;
}

/deep/ .el-dialog__title {
  color: #ffffff;
}

/deep/ .el-dialog__headerbtn i {
  color: #ffffff;
  -webkit-text-stroke-color: #ffffff;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/InappTestSendDialog.vue